import { z } from 'zod'
import { addDays, daysBetween, type LocalDate } from './dates.js'
import { gorevTargetFor } from './economy.js'
import {
  GAME_IDS,
  type GameId,
  LESSON_IDS,
  type LessonId,
  type Mode,
  recapQuestionIdOf,
  STATIC_RECAP_QUESTION_IDS,
  type StaticRecapQuestionId,
} from './ids.js'

/**
 * Günlük Görevler (W3-OVERHAUL §2.5). Three tasks a day, generated from `(childId, localDate)`
 * and nothing else that moves, so the client and the server fold to the same three items
 * (PRIV-06) with no stored state. Progress is not here: the fold derives it from the day's
 * events (ARCHITECTURE §4.5 rule 15).
 */

export type BrushGorev = {
  id: 'g-brush-sabah' | 'g-brush-aksam' | 'g-brush-tamgun'
  kind: 'brush'
  window: 'morning' | 'evening' | 'both'
}
export type QuestionGorev = { id: 'g-soru'; kind: 'question'; questionId: StaticRecapQuestionId }
/** The `ders` fallback: nothing is finished yet, so today's task is the next lesson. */
export type DersGorev = { id: 'g-ders'; kind: 'question'; lessonId: LessonId }
export type GameGorev = { id: 'g-oyun'; kind: 'game'; gameId: GameId; target: number }

export type Gorev = BrushGorev | QuestionGorev | DersGorev | GameGorev
export type DailyGorevler = [BrushGorev, QuestionGorev | DersGorev, GameGorev]

export const BrushGorevSchema = z.strictObject({
  id: z.enum(['g-brush-sabah', 'g-brush-aksam', 'g-brush-tamgun']),
  kind: z.literal('brush'),
  window: z.enum(['morning', 'evening', 'both']),
})
export const QuestionGorevSchema = z.discriminatedUnion('id', [
  z.strictObject({
    id: z.literal('g-soru'),
    kind: z.literal('question'),
    questionId: z.enum(STATIC_RECAP_QUESTION_IDS),
  }),
  z.strictObject({
    id: z.literal('g-ders'),
    kind: z.literal('question'),
    lessonId: z.enum(LESSON_IDS),
  }),
])
export const GameGorevSchema = z.strictObject({
  id: z.literal('g-oyun'),
  kind: z.literal('game'),
  gameId: z.enum(GAME_IDS),
  target: z.number().int().positive(),
})
export const DailyGorevlerSchema = z.tuple([BrushGorevSchema, QuestionGorevSchema, GameGorevSchema])

/** FNV-1a 32 bit over the UTF-8 bytes. The same function runs on the server. */
export function fnv1a(text: string): number {
  let hash = 0x811c9dc5
  const mix = (byte: number) => {
    hash ^= byte
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  // UTF-8 by hand: ids and dates are ASCII, but a child id is whatever the caller passes.
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0
    if (code < 0x80) mix(code)
    else if (code < 0x800) {
      mix(0xc0 | (code >> 6))
      mix(0x80 | (code & 0x3f))
    } else if (code < 0x10000) {
      mix(0xe0 | (code >> 12))
      mix(0x80 | ((code >> 6) & 0x3f))
      mix(0x80 | (code & 0x3f))
    } else {
      mix(0xf0 | (code >> 18))
      mix(0x80 | ((code >> 12) & 0x3f))
      mix(0x80 | ((code >> 6) & 0x3f))
      mix(0x80 | (code & 0x3f))
    }
  }
  return hash >>> 0
}

export function gorevSeed(childId: string, localDate: LocalDate): number {
  return fnv1a(`${childId}:${localDate}`)
}

/** One well mixed number per slot of a day, so the three picks do not move together. */
function roll(seed: number, salt: string): number {
  return fnv1a(`${seed}:${salt}`)
}

const EPOCH: LocalDate = '1970-01-01'

/**
 * "Never yesterday's" without remembering yesterday: even days take their raw pick, odd days
 * pick among what neither neighbour's raw pick is. Both neighbours are even, so their picks
 * are raw and known from their own seed, and no two consecutive days share a value as long as
 * the pool holds at least three entries.
 */
function pickNoRepeat<T>(
  pool: readonly T[],
  childId: string,
  localDate: LocalDate,
  salt: string,
): T {
  const raw = (date: LocalDate) => pool[roll(gorevSeed(childId, date), salt) % pool.length] as T
  const dayIndex = daysBetween(EPOCH, localDate)
  if (pool.length === 1) return raw(localDate)
  // Two entries alternate, offset per child so two children do not march in step.
  if (pool.length === 2) return pool[(dayIndex + fnv1a(`${childId}:${salt}`)) % 2] as T
  if (dayIndex % 2 === 0) return raw(localDate)
  const taken = new Set([raw(addDays(localDate, -1)), raw(addDays(localDate, 1))])
  const candidates = pool.filter((item) => !taken.has(item))
  return candidates[roll(gorevSeed(childId, localDate), salt) % candidates.length] as T
}

export type GorevOptions = {
  /** Mini asks for a smaller game score (economy.ts `gorevTargetFor`). */
  mode?: Mode
}

/**
 * The three tasks of a date. `lessonsDone` is the lessons the child has finished (cards
 * completed) in any order; the question pool is those lessons' recap questions. With an
 * empty pool the question slot becomes the `ders` fallback, the next lesson in order.
 */
export function dailyGorevler(
  childId: string,
  localDate: LocalDate,
  lessonsDone: readonly LessonId[],
  options: GorevOptions = {},
): DailyGorevler {
  const mode = options.mode ?? 'core'
  const seed = gorevSeed(childId, localDate)
  const dayIndex = daysBetween(EPOCH, localDate)

  // Brush: every third day may be tam gün (seed decides), the rest alternate by day parity
  // mixed with the seed so two children do not march in step.
  let brush: BrushGorev
  if (dayIndex % 3 === 0 && roll(seed, 'brush') % 2 === 0) {
    brush = { id: 'g-brush-tamgun', kind: 'brush', window: 'both' }
  } else if (((dayIndex % 2) + (roll(seed, 'brush') % 2)) % 2 === 0) {
    brush = { id: 'g-brush-sabah', kind: 'brush', window: 'morning' }
  } else {
    brush = { id: 'g-brush-aksam', kind: 'brush', window: 'evening' }
  }

  const pool = LESSON_IDS.filter((id) => lessonsDone.includes(id))
  let question: QuestionGorev | DersGorev
  if (pool.length === 0) {
    const next = LESSON_IDS.find((id) => !lessonsDone.includes(id)) ?? 'p1'
    question = { id: 'g-ders', kind: 'question', lessonId: next }
  } else {
    const lessonId = pickNoRepeat(pool, childId, localDate, 'question')
    question = { id: 'g-soru', kind: 'question', questionId: recapQuestionIdOf(lessonId) }
  }

  const gameId = pickNoRepeat(GAME_IDS, childId, localDate, 'game')
  const game: GameGorev = {
    id: 'g-oyun',
    kind: 'game',
    gameId,
    target: gorevTargetFor(gameId, mode),
  }

  return [brush, question, game]
}
